import { ButtonMenu, GridTableApi, MenuItem, useModal } from "@homebound/beam";
import { useMemo } from "react";
import { useHistory } from "react-router-dom";
import {
  DocumentDetailDocumentTypeFragment,
  NamedDevelopmentCohortsProjectsFragment,
} from "src/fragments/circularFragments.generated";
import { ConfirmationModal } from "src/routes/components/ConfirmationModal";
import { DeleteConfirmationModal } from "src/routes/components/DeleteConfirmationModal";
import { useProjectContext } from "src/routes/projects/context/ProjectContext";
import { DocumentsPageMode, maybeNavigateToNewDraft } from "src/routes/projects/documents/DocumentsPage";
import {
  DocumentsPageDocumentDetailFragment,
  useDeleteDocumentsMutation,
  useDocumentsAssetQuery,
  useSaveDocumentsMutation,
} from "src/routes/projects/documents/DocumentsPage.generated";
import { RecategorizeModal } from "src/routes/projects/documents/RecategorizeModal";
import { downloadFilesToZip } from "src/utils/downloader";

type DocumentsActionsMenuProps = {
  mode: DocumentsPageMode;
  versionId?: string;
  groupBy?: string;
  selectedDocuments: DocumentsPageDocumentDetailFragment[];
  documentTypes: DocumentDetailDocumentTypeFragment[];
  tableRefetch: () => void;
  tableApi: GridTableApi<any>;
  development?: NamedDevelopmentCohortsProjectsFragment;
};

export function DocumentsActionsMenu(props: DocumentsActionsMenuProps) {
  const { selectedDocuments, documentTypes, tableRefetch, tableApi, development, mode, versionId, groupBy } = props;
  const [deleteDocumentsMutation] = useDeleteDocumentsMutation();
  const [saveDocumentsMutation] = useSaveDocumentsMutation();
  const { openModal, closeModal } = useModal();
  const { name } = useProjectContext();
  const documentIds = selectedDocuments.map(({ id }) => id);
  const query = useDocumentsAssetQuery({ variables: { filter: { id: documentIds, versionId } } });
  const documentsForDownload = query.data?.documents;

  const editableDocs = selectedDocuments.filter((doc) => doc.canEdit.allowed).sortByKey("displayName");

  const canArchiveAllowed = selectedDocuments.filter((doc) => doc.canArchive?.allowed);
  const [unarchive, archive] = canArchiveAllowed.partition((doc) => doc.isDeleted === true);

  const deletable = selectedDocuments.filter((doc) => doc.canDelete?.allowed);

  const history = useHistory();

  const menuItems: MenuItem[] = useMemo(
    () => {
      const archiveVerb = mode === DocumentsPageMode.ReadyPlan ? "Remove" : "Archive";
      const archiveAction = {
        label: archiveVerb,
        disabled: archive.isEmpty,
        onClick: () =>
          openModal({
            content: (
              <ConfirmationModal
                confirmationMessage={
                  <>
                    <p>{`Are you sure you want to ${archiveVerb.toLowerCase()} ${archive.length} document(s)`}</p>
                    <ul>
                      {archive.map((doc) => (
                        <li key={doc.id}>{doc.displayName}</li>
                      ))}
                    </ul>
                  </>
                }
                onConfirmAction={async () => {
                  const result = await saveDocumentsMutation({
                    variables: { input: archive.map((doc) => ({ id: doc.id, deleted: true })) },
                  });
                  const parents = result.data?.saveDocuments.documents.map((d) => d.parent) ?? [];
                  // if a new draft was created by our unremoval, we should navigate to it
                  if (maybeNavigateToNewDraft(history, versionId, parents, { groupBy })) return;
                  // Refetch to update our cache.
                  tableRefetch();
                  tableApi.clearSelections();
                  closeModal();
                }}
                title={`${archiveVerb} Documents`}
                label={archiveVerb}
              />
            ),
          }),
      };

      const unArchiveAction = {
        label: `Un${archiveVerb}`,
        disabled: unarchive.isEmpty,
        onClick: () =>
          openModal({
            content: (
              <ConfirmationModal
                confirmationMessage={
                  <>
                    <p>{`Are you sure you want to un-${archiveVerb.toLowerCase()} ${unarchive.length} document(s)`}</p>
                    <ul>
                      {unarchive.map((doc) => (
                        <li key={doc.id}>{doc.displayName}</li>
                      ))}
                    </ul>
                  </>
                }
                onConfirmAction={async () => {
                  const result = await saveDocumentsMutation({
                    variables: { input: unarchive.map((doc) => ({ id: doc.id, deleted: false })) },
                  });
                  const parents = result.data?.saveDocuments.documents.map((d) => d.parent) ?? [];
                  // if a new draft was created by our unremoval, we should navigate to it
                  if (maybeNavigateToNewDraft(history, versionId, parents, { groupBy })) return;
                  // Refetch to update our cache.
                  tableRefetch();
                  closeModal();
                }}
                title={`Un${archiveVerb.toLowerCase()} Documents`}
                label={`Un${archiveVerb}`}
              />
            ),
          }),
      };

      const deleteAction = {
        label: "Delete",
        disabled: deletable.isEmpty,
        onClick: () =>
          openModal({
            content: (
              <DeleteConfirmationModal
                onConfirmDelete={async () => {
                  // if we are on a ready plan, then deletion could trigger a new version
                  const idsToDelete = editableDocs.map((doc) => doc.id);
                  await deleteDocumentsMutation({ variables: { input: { id: idsToDelete } } });
                  tableApi.deleteRows(idsToDelete);
                  // Refetch to update our cache.
                  tableRefetch();
                }}
                entityType={"Documents"}
                confirmationMessage={
                  <>
                    <p>The following documents will be deleted. This cannot be undone.</p>
                    <ul>
                      {editableDocs.map((doc) => (
                        <li key={doc.id}>{doc.displayName}</li>
                      ))}
                    </ul>
                  </>
                }
              />
            ),
          }),
      };

      return [
        ...(archiveAction.disabled ? [] : [archiveAction]),
        ...(unArchiveAction.disabled ? [] : [unArchiveAction]),
        deleteAction,
        {
          label: "Recategorize",
          disabled: editableDocs.isEmpty,
          onClick: () =>
            openModal({
              content: (
                <RecategorizeModal
                  versionId={versionId}
                  groupBy={groupBy}
                  documents={editableDocs}
                  documentTypes={documentTypes}
                  development={development}
                />
              ),
            }),
        },
        {
          label: "Download",
          disabled: selectedDocuments.isEmpty,
          onClick: async () => {
            if (documentsForDownload) {
              await downloadFilesToZip(
                `${name} Documents.zip`,
                documentsForDownload.map(({ name, asset }) => ({
                  filename: asset.fileName ?? name,
                  url: asset.downloadUrl,
                })),
              );
            }
          },
        },
      ];
    },
    // TODO: validate this eslint-disable. It was automatically ignored as part of https://app.shortcut.com/homebound-team/story/40033/enable-react-hooks-exhaustive-deps-for-internal-frontend
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editableDocs, versionId, archive, unarchive, selectedDocuments, deleteDocumentsMutation],
  );

  return <ButtonMenu trigger={{ label: "Actions" }} items={menuItems} />;
}
